import { Hero, Layout } from '@components/common'
import { Button, Container } from '@components/ui'
import { Breadcrumb } from '@types'
import { GetStaticPropsContext } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { FormEvent, useState } from 'react'
import { toast } from 'react-toastify'
import s from './contact.module.css'

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(locale || 'en-US', ['common', 'contact', 'cart']))
    }
  }
}

export default function Contact() {
  const { t } = useTranslation(['common', 'contact', 'cart'])

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [contactMeByFaxOnly, setContactMeByFaxOnly] = useState(0)
  const [messageSent, setMessageSent] = useState(false)
  const [isSendingMessage, setIsSendingMessage] = useState(false)

  const breadcrumbs: Array<Breadcrumb> = [
    { path: '/', label: t('common:breadcrumbs.home') },
    { label: t('common:breadcrumbs.contact') },
  ]

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()

    if (messageSent) {
      return
    }

    setIsSendingMessage(true)

    try {
      const res = await fetch("/api/sendgrid", {
        body: JSON.stringify({
          email,
          fullName,
          subject,
          message,
          contactMeByFaxOnly
        }),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      });

      if (res.status === 403) {
        toast.error('contact:errors.robot')
        return
      }

      if (res.status === 400) {
        const { message } = await res.json()

        console.error(message)
        toast.error(t('contact:errors.invalidData'))
        return
      }

      setFullName('')
      setEmail('')
      setSubject('')
      setMessage('')
      setIsSendingMessage(false)
      setMessageSent(true)
      setTimeout(() => setMessageSent(false), 5000)
    } catch(error) {
      console.error(error)
      toast.error(t('contact:errors.unknown'))
    }
  }

  return (
    <>
      <Head>
        <title>{t('common:metaTitles.contact')}</title>
        <meta name="description" content={t('common:metaDescriptions.contact')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t(`common:metaTitles.contact`)} />
        <meta property="og:image" content="https://ennsens.com/assets/meta/og-image.jpg" />
        <meta property="og:image:alt" content={t('common:slogan')} />
        <meta property="og:description" content={t('common:metaDescriptions.contact')} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={t(`common:metaTitles.contact`)} />
        <meta name="twitter:description" content={t('common:metaDescriptions.contact')} />
        <meta name="twitter:image" content="https://ennsens.com/assets/meta/og-image.jpg" />
        <meta name="twitter:image:alt" content={t('common:slogan')} />
      </Head>
      <Hero title={t('contact:pageTitle')} breadcrumbs={breadcrumbs} />
      <Container className="my-20 md:my-32 flex flex-col md:flex-row gap-16 items-center">
        <section className="w-full md:w-1/2 card">
          <h2 className="text-2xl md:text-4xl">{t('contact:contactForm.title')}</h2>
          <form className="grid gap-4 md:gap-8 content-start mt-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
              <label className="form-label">
                {t('common:labels.fullName')}
                <input className="form-input" name="fullName" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </label>
              <label className="form-label">
                {t('common:labels.email')}
                <input className="form-input" name="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
            </div>
            <div className="lg:w-1/2 lg:pr-4">
              <label className="form-label">
                {t('common:labels.subject')}
                <input className="form-input" name="subject" required value={subject} onChange={(e) => setSubject(e.target.value)} />
              </label>
            </div>
            <label className="form-label">
              {t('common:labels.message')}
              <textarea rows={4} name="message" className="form-textarea" required value={message} onChange={(e) => setMessage(e.target.value)} />
            </label>
            <input type="checkbox" name="contact_me_by_fax_only" className="sr-only" tabIndex={-1} autoComplete="off" value={contactMeByFaxOnly} onChange={(e) => setContactMeByFaxOnly(parseInt(e.target.value, 10))} />
            {messageSent ? <p className="text-green text-xl text-center">Message sent</p> : <Button type="submit" loading={isSendingMessage}>{t('common:labels.send')}</Button>}
          </form>
        </section>
        <section className="md:w-1/2 leading-7">
          <div className="flex gap-2">
            <h3 className="text-xl">{t('contact:contacts.contacts')}</h3>
            <div>
              <a href="mailto:info@ennsens.cz">info@ennsens.com</a>
            </div>
          </div>
          <div className="mt-8 flex gap-2">
            <h3 className="text-xl">{t('contact:contacts.address')}</h3>
            <div>
              <p>Češkova 1240</p>
              <p>530 02, Pardubice - Zelené Předměstí</p>
              <p>{t('common:countries.cs-CZ')}</p>
            </div>
          </div>
          <div className="mt-8 flex gap-2">
            <h3 className="text-xl">{t('contact:contacts.socialNetworks')}</h3>
            <div className="flex items-center gap-3">
              <a href="https://www.facebook.com/enn.sens" target="_blank" rel="noreferrer">FB</a>
              <div className={s.socialsDivider} />
              <a href="https://www.instagram.com/enn.sens" target="_blank" rel="noreferrer">INS</a>
            </div>
          </div>
        </section>
      </Container>
    </>
  )
}

Contact.Layout = Layout
